import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {
  CheckoutBillInterface,
  PaymentMethodDataProps,
} from "_interfaces/_common/payment";
import { CheckoutModel } from "_models/data/data.checkout.model";
import { ApplyCouponModel } from "_models/data/payment/data.applyCoupon.model";
import { CheckoutErrorModel } from "_models/errors/error.checkout.model";
import { ApplyCouponErrorModel } from "_models/errors/payment/error.applyCoupon.model";
import MobileWithManPic from "assets/img/static/bg/mobile_with_man.png";
import ProcessStatusPanel, {
  ProcessStatusPanelProps,
} from "component/_common/status/processStatusPanel";
import RouteConstant from "navigation/constant";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import ApplyCouponForm from "./applyCouponForm";
import PaymentCheckoutBillTable from "./paymentCheckoutBillTable";
import PaymentMethod from "./paymentMethod";
import { PaymentOptionsListProps } from "./paymentOptionsList";

interface Props {
  checkoutBill?: CheckoutBillInterface[];
  handleMakePaymentRequest?: (DATA: PaymentMethodDataProps) => void;
  togglePaymentCheckout?: () => void;
  cancelPaymentCheckout?: () => void;
  isCouponFormEnabled?: boolean;
  couponState?: ApplyCouponModel;
  setCouponState?: React.Dispatch<React.SetStateAction<ApplyCouponModel>>;
  couponErrors?: ApplyCouponErrorModel;
  setCouponErrors?: React.Dispatch<React.SetStateAction<ApplyCouponErrorModel>>;
  handleApplyCoupon?: (DATA: ApplyCouponModel) => void;
  handleRemoveCoupon?: () => void;
  isSubmitWithoutPayment?: boolean;
  handleSubmitWithoutPayment?: () => void;
  checkoutTitle?: string;
  disableDialog?: boolean;
  disableAgreement?: boolean;
  disableAction?: boolean;
  paymentOptions?: PaymentOptionsListProps[];
  showOnlyPaymentMethod?: boolean;
  /** When set, replaces the payment-method column with inline process status (e.g. fulfillment polling). */
  embeddedPaymentProcess?: Omit<ProcessStatusPanelProps, "variant"> | null;
}

const PaymentCheckout: React.FC<Props> = ({
  checkoutBill,
  handleMakePaymentRequest,
  togglePaymentCheckout,
  cancelPaymentCheckout,
  isCouponFormEnabled,
  couponState,
  setCouponState,
  couponErrors,
  setCouponErrors,
  handleApplyCoupon,
  handleRemoveCoupon,
  isSubmitWithoutPayment,
  handleSubmitWithoutPayment,
  checkoutTitle = "To pay",
  disableDialog = false,
  disableAgreement = false,
  disableAction = false,
  paymentOptions,
  showOnlyPaymentMethod = false,
  embeddedPaymentProcess = null,
}) => {
  const [state, setState] = useState<CheckoutModel>(new CheckoutModel());
  const [errors, setErrors] = useState<CheckoutErrorModel>(
    new CheckoutErrorModel()
  );

  const [openPaymentOption, setOpenPaymentOption] = useState<boolean>(false);

  const handleOpenPaymentOption = () => {
    if (!state?.agreeTerms) {
      setErrors((prev) => ({
        ...prev,
        agreeTerms: "You must agree to the terms and conditions.",
      }));

      return;
    }
    setOpenPaymentOption(true);
  };

  const handleClosePaymentOption = () => {
    if (openPaymentOption) {
      setOpenPaymentOption(false);
    }
    if (showOnlyPaymentMethod && togglePaymentCheckout) {
      togglePaymentCheckout();
    }
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6} bgcolor="primaryVar.main">
          <Box
            px={2}
            py={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography textAlign="center" variant="body2" mb={3}>
              Just few steps, then <br /> you can connect with <br /> your loved
              ones
            </Typography>
            <div className="resImg">
              <img src={MobileWithManPic} alt="MobileWithManPic" />
            </div>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {embeddedPaymentProcess ? (
            <Box p={{ xs: 1, sm: 2 }}>
              <ProcessStatusPanel
                variant="embedded"
                {...embeddedPaymentProcess}
              />
            </Box>
          ) : (openPaymentOption || showOnlyPaymentMethod) &&
            handleMakePaymentRequest ? (
            <>
              <PaymentMethod
                handleMakePaymentRequest={handleMakePaymentRequest}
                togglePaymentOption={handleClosePaymentOption}
                paymentOptions={paymentOptions}
              />
            </>
          ) : (
            <>
              {!disableDialog ? (
                <>
                  <Box
                    p={{ xs: 1, sm: 2 }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: 1,
                        p: {
                          xs: 1,
                          sm: 2,
                        },
                      }}
                    >
                      <CheckoutContent
                        isSubmitWithoutPayment={isSubmitWithoutPayment}
                        checkoutTitle={checkoutTitle}
                        checkoutBill={checkoutBill}
                        isCouponFormEnabled={isCouponFormEnabled}
                        couponState={couponState}
                        setCouponState={setCouponState}
                        couponErrors={couponErrors}
                        setCouponErrors={setCouponErrors}
                        handleApplyCoupon={handleApplyCoupon}
                        handleRemoveCoupon={handleRemoveCoupon}
                      />

                      {!disableAgreement ? (
                        <CheckoutAgreement
                          state={state}
                          setState={setState}
                          errors={errors}
                          setErrors={setErrors}
                        />
                      ) : null}
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <CheckoutContent
                    isSubmitWithoutPayment={isSubmitWithoutPayment}
                    checkoutTitle={checkoutTitle}
                    checkoutBill={checkoutBill}
                    isCouponFormEnabled={isCouponFormEnabled}
                    couponState={couponState}
                    setCouponState={setCouponState}
                    couponErrors={couponErrors}
                    setCouponErrors={setCouponErrors}
                    handleApplyCoupon={handleApplyCoupon}
                    handleRemoveCoupon={handleRemoveCoupon}
                  />
                  {!disableAgreement ? (
                    <CheckoutAgreement
                      state={state}
                      setState={setState}
                      errors={errors}
                      setErrors={setErrors}
                    />
                  ) : null}
                  {!disableAction ? (
                    <Box pt={3} className="tw:flex tw:justify-end tw:gap-2">
                      <CheckoutAction
                        togglePaymentCheckout={togglePaymentCheckout}
                        cancelPaymentCheckout={cancelPaymentCheckout}
                        isSubmitWithoutPayment={isSubmitWithoutPayment}
                        handleSubmitWithoutPayment={handleSubmitWithoutPayment}
                        state={state}
                        setErrors={setErrors}
                        handleOpenPaymentOption={handleOpenPaymentOption}
                      />
                    </Box>
                  ) : null}
                </>
              )}

              {!disableDialog ? (
                <Box p={{ xs: 1, sm: 2 }}>
                  <CheckoutAction
                    togglePaymentCheckout={togglePaymentCheckout}
                    cancelPaymentCheckout={cancelPaymentCheckout}
                    isSubmitWithoutPayment={isSubmitWithoutPayment}
                    handleSubmitWithoutPayment={handleSubmitWithoutPayment}
                    state={state}
                    setErrors={setErrors}
                    handleOpenPaymentOption={handleOpenPaymentOption}
                  />
                </Box>
              ) : null}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default PaymentCheckout;

interface CheckoutContentProps {
  isSubmitWithoutPayment?: boolean;
  checkoutTitle?: string;
  checkoutBill?: CheckoutBillInterface[];
  isCouponFormEnabled?: boolean;
  couponState?: ApplyCouponModel;
  setCouponState?: React.Dispatch<React.SetStateAction<ApplyCouponModel>>;
  couponErrors?: ApplyCouponErrorModel;
  setCouponErrors?: React.Dispatch<React.SetStateAction<ApplyCouponErrorModel>>;
  handleApplyCoupon?: (DATA: ApplyCouponModel) => void;
  handleRemoveCoupon?: () => void;
}
const CheckoutContent: React.FC<CheckoutContentProps> = ({
  isSubmitWithoutPayment,
  checkoutTitle,
  checkoutBill,
  isCouponFormEnabled,
  couponState,
  setCouponState,
  couponErrors,
  setCouponErrors,
  handleApplyCoupon,
  handleRemoveCoupon,
}) => {
  return (
    <>
      {isSubmitWithoutPayment ? (
        <Box>
          <Typography fontWeight={400}>
            Are you sure you want to complete this process? <br />
            Click <Box component="strong">Complete Process</Box> to finalize.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography mb={1} fontWeight={500}>
            {checkoutTitle}
          </Typography>
          <Box>
            {checkoutBill?.length ? (
              <PaymentCheckoutBillTable checkoutBill={checkoutBill} />
            ) : null}

            {isCouponFormEnabled &&
            couponState &&
            setCouponState &&
            couponErrors &&
            setCouponErrors ? (
              <ApplyCouponForm
                handleApplyCoupon={handleApplyCoupon}
                handleRemoveCoupon={handleRemoveCoupon}
                couponState={couponState}
                setCouponState={setCouponState}
                couponErrors={couponErrors}
                setCouponErrors={setCouponErrors}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </>
  );
};

interface CheckoutAgreementProps {
  state: CheckoutModel;
  setState: React.Dispatch<React.SetStateAction<CheckoutModel>>;
  errors: CheckoutErrorModel;
  setErrors: React.Dispatch<React.SetStateAction<CheckoutErrorModel>>;
}

const CheckoutAgreement: React.FC<CheckoutAgreementProps> = ({
  state,
  setState,
  errors,
  setErrors,
}) => {
  const { palette } = useTheme();
  return (
    <>
      <Box pt={3} fontSize={14}>
        <FormControlLabel
          onChange={(_, checked) => {
            setErrors((prev) => ({
              ...prev,
              agreeTerms: "",
            }));
            setState((prev) => ({
              ...prev,
              agreeTerms: checked,
            }));
          }}
          control={<Checkbox checked={state?.agreeTerms} />}
          label={
            <Typography fontSize="inherit">
              I agree with the &nbsp;
              <Link
                to={RouteConstant.TERMS_CONDITIONS}
                target="_blank"
                style={{
                  color: palette.primary.main,
                }}
              >
                Terms and Conditions
              </Link>{" "}
              &{" "}
              <Link
                to={RouteConstant.PRIVACY_POLICY}
                target="_blank"
                style={{
                  color: palette.primary.main,
                }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        {errors?.agreeTerms ? (
          <FormHelperText sx={{ color: palette.error.main }}>
            {errors.agreeTerms}
          </FormHelperText>
        ) : (
          <></>
        )}
      </Box>
    </>
  );
};

interface CheckoutActionProps {
  togglePaymentCheckout?: () => void;
  cancelPaymentCheckout?: () => void;
  isSubmitWithoutPayment?: boolean;
  handleSubmitWithoutPayment?: () => void;
  state: CheckoutModel;
  setErrors: React.Dispatch<React.SetStateAction<CheckoutErrorModel>>;
  handleOpenPaymentOption: () => void;
}
const CheckoutAction: React.FC<CheckoutActionProps> = ({
  togglePaymentCheckout,
  cancelPaymentCheckout,
  isSubmitWithoutPayment,
  handleSubmitWithoutPayment,
  state,
  setErrors,
  handleOpenPaymentOption,
}) => {
  const { palette } = useTheme();
  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        {cancelPaymentCheckout ? (
          <Button
            variant="text"
            color="inherit"
            onClick={cancelPaymentCheckout}
          >
            Cancel
          </Button>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            ml: "auto",
          }}
        >
          {togglePaymentCheckout ? (
            <Button
              variant="text"
              color="inherit"
              onClick={togglePaymentCheckout}
            >
              Back
            </Button>
          ) : null}

          {isSubmitWithoutPayment ? (
            <Button
              variant="contained"
              color="inherit"
              onClick={() => {
                if (!state?.agreeTerms) {
                  setErrors((prev) => ({
                    ...prev,
                    agreeTerms: "You must agree to the terms and conditions.",
                  }));

                  return;
                }
                if (handleSubmitWithoutPayment) handleSubmitWithoutPayment();
              }}
              sx={{
                bgcolor: palette.secondary.main,
                color: palette.secondary.contrastText,
              }}
            >
              Complete Process
            </Button>
          ) : (
            <Button
              variant="contained"
              color="inherit"
              onClick={handleOpenPaymentOption}
              sx={{
                bgcolor: palette.secondary.main + "!important",
                color: palette.secondary.contrastText,
              }}
            >
              Pay now
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};
